import CustomTooltip from '../../../shared/CustomTooltip';
import {
    ArrowForward as ArrowForwardIcon,
    ChevronLeft,
    ChevronRight,
} from '@mui/icons-material';
import {
    Box,
    Button,
    Collapse,
    Grid,
    IconButton,
    Stack,
    ToggleButton,
    ToggleButtonGroup,
    Typography,
    useMediaQuery,
    useTheme,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import {
    addDays,
    addMonths,
    endOfMonth,
    endOfWeek,
    format,
    startOfMonth,
    startOfWeek,
} from 'date-fns';
import React, { useCallback, useMemo, useState } from 'react';
import { useFormContext, useWatch } from 'react-hook-form';
import { formatCurrencyShort } from '../../../../shared/utils';
import {
    BaseExperienceCharge,
    ExperienceCharge,
    FlexibleExperienceCharge,
    FlexiblePriceOverride,
    PriceOverride,
} from '../../../types/experience';
import PriceOverrideModal, {
    PriceOverrideFormData,
} from './PriceOverrideModal';
import RatesMonthCalendar, { renderRateLabel } from './RatesMonthCalendar';
import { FeeType } from '../../../enums/experience';
import { useFormState } from '../Form/useFormState';

export type RateLabel = {
    name: FeeType;
    value: number;
};

const buildCalendarDays = (month: Date) => {
    const start = startOfWeek(startOfMonth(month));
    const end = endOfWeek(endOfMonth(month));
    const days: Date[] = [];

    let current = start;

    while (current <= end) {
        days.push(current);
        current = addDays(current, 1);
    }

    return days;
};

const buildYearMonths = (year: number, min: Date, max: Date) => {
    const months: Date[] = [];

    for (let monthIndex = 0; monthIndex < 12; monthIndex += 1) {
        const monthDate = startOfMonth(new Date(year, monthIndex, 1));

        if (monthDate < min || monthDate > max) continue;

        months.push(monthDate);
    }

    return months;
};

const RatesCalendar = () => {
    const { experience } = useFormState();
    const theme = useTheme();
    const isTablet = useMediaQuery(theme.breakpoints.down('md'));
    const { control, setValue } = useFormContext();
    const charges = useWatch({
        control,
        name: 'charges',
    }) as ExperienceCharge[];

    const price = charges?.find(
        (charge) =>
            charge?.chargeType === 'price' ||
            charge?.chargeType === 'flexiblePrice',
    );

    const prices = charges?.filter(
        (charge) => charge?.chargeType === 'price',
    ) as BaseExperienceCharge[];

    const isFlexiblePrice: boolean = price?.chargeType === 'flexiblePrice';

    const minHireHours = useWatch({ control, name: 'minHireHours' });
    const shouldShowHalfDayBilling =
        experience.type === 'commercial' && minHireHours < 24;

    const initialMonth = startOfMonth(new Date());
    const [visibleMonth, setVisibleMonth] = useState<Date>(initialMonth);
    const [yearViewYear, setYearViewYear] = useState(
        initialMonth.getFullYear(),
    );
    const [viewMode, setViewMode] = useState<'month' | 'year'>('month');
    const [selectionMode, setSelectionMode] = useState<'range' | 'individual'>(
        'range',
    );
    const [rangeStart, setRangeStart] = useState<Date | null>(null);
    const [rangeEnd, setRangeEnd] = useState<Date | null>(null);
    const [hoverDate, setHoverDate] = useState<Date | null>(null);
    const [individualDates, setIndividualDates] = useState<Date[]>([]);
    const [selectedMonths, setSelectedMonths] = useState<Date[]>([]);
    const [modalOpen, setModalOpen] = useState(false);
    const [showSeasonalPricing, setShowSeasonalPricing] = useState(false);
    const today = new Date();
    const currentYear = today.getFullYear();
    const minVisibleMonth = startOfMonth(new Date(currentYear - 1, 0, 1));
    const maxVisibleMonth = startOfMonth(new Date(currentYear + 1, 11, 1));
    const yearOptions = [currentYear - 1, currentYear, currentYear + 1];

    const getSelectedDatesFromRange = (): Date[] => {
        if (!rangeStart) return [];
        if (!rangeEnd) return [rangeStart];

        const dates: Date[] = [];
        const start = rangeStart < rangeEnd ? rangeStart : rangeEnd;
        const end = rangeStart < rangeEnd ? rangeEnd : rangeStart;

        let current = new Date(start);
        while (current <= end) {
            dates.push(new Date(current));
            current = addDays(current, 1);
        }

        return dates;
    };

    const getSelectedDatesFromMonths = (): Date[] => {
        if (selectedMonths.length === 0) return [];

        const dates: Date[] = [];
        selectedMonths.forEach((month) => {
            const start = startOfMonth(month);
            const end = endOfMonth(month);

            let current = new Date(start);
            while (current <= end) {
                dates.push(new Date(current));
                current = addDays(current, 1);
            }
        });

        return dates;
    };

    const hasYearViewDateSelection =
        viewMode === 'year' &&
        (individualDates.length > 0 || rangeStart !== null);

    const selectedDates =
        viewMode === 'month'
            ? selectionMode === 'range'
                ? getSelectedDatesFromRange()
                : individualDates
            : hasYearViewDateSelection
              ? selectionMode === 'range'
                  ? getSelectedDatesFromRange()
                  : individualDates
              : getSelectedDatesFromMonths();

    const handleClearSelection = () => {
        setRangeStart(null);
        setRangeEnd(null);
        setHoverDate(null);
        setIndividualDates([]);
        setSelectedMonths([]);
    };

    const handleToggleSeasonalPricing = () => {
        if (showSeasonalPricing) {
            handleClearSelection();
            setModalOpen(false);
        }

        setShowSeasonalPricing((prev) => !prev);
    };

    const handleSelectionModeChange = (
        _: React.MouseEvent<HTMLElement>,
        nextMode: 'range' | 'individual' | null,
    ) => {
        if (!nextMode) return;

        if (nextMode === 'individual' && selectionMode === 'range') {
            const rangeDates = getSelectedDatesFromRange();
            setIndividualDates(rangeDates);
            setRangeStart(null);
            setRangeEnd(null);
            setHoverDate(null);
        } else if (nextMode === 'range' && selectionMode === 'individual') {
            setRangeStart(null);
            setRangeEnd(null);
            setHoverDate(null);
            setIndividualDates([]);
        }

        setSelectionMode(nextMode);
    };

    const clearDateSelection = () => {
        setRangeStart(null);
        setRangeEnd(null);
        setHoverDate(null);
        setIndividualDates([]);
    };

    const handleMonthToggle = (monthDate: Date, event: React.MouseEvent) => {
        event.stopPropagation();

        clearDateSelection();

        const monthKey = format(monthDate, 'yyyy-MM');
        const isSelected = selectedMonths.some(
            (m) => format(m, 'yyyy-MM') === monthKey,
        );

        if (isSelected) {
            setSelectedMonths((prev) =>
                prev.filter((m) => format(m, 'yyyy-MM') !== monthKey),
            );
        } else {
            setSelectedMonths((prev) => [...prev, monthDate]);
        }
    };

    const isMonthSelected = (monthDate: Date) => {
        const monthKey = format(monthDate, 'yyyy-MM');
        return selectedMonths.some((m) => format(m, 'yyyy-MM') === monthKey);
    };

    const handleSaveOverride = (override: PriceOverrideFormData) => {
        if (experience.type === 'commercial') {
            const commercialFieldMap: Record<
                string,
                keyof PriceOverrideFormData
            > = {
                perDeskPerDay: 'desk',
                perDeskPerHalfDay: 'deskHalfDay',
                perOfficePerDay: 'privateOffice',
                perOfficePerHalfDay: 'privateOfficeHalfDay',
                perMeetingRoomPerDay: 'meetingRoom',
                perMeetingRoomPerHalfDay: 'meetingRoomHalfDay',
                entireVenuePerDay: 'entireVenue',
                entireVenuePerHalfDay: 'entireVenueHalfDay',
            };

            const dateString = selectedDates.map((d) =>
                format(d, 'yyyy-MM-dd'),
            );

            const updatedCharges = (charges as BaseExperienceCharge[]).map(
                (charge) => {
                    const fieldKey = commercialFieldMap[charge.type];
                    if (!fieldKey) return charge;

                    const rawValue = override[fieldKey];
                    const numericValue = parseFloat(rawValue);
                    const existingOverrides = charge.priceOverrides ?? [];

                    if (isNaN(numericValue)) {
                        return charge;
                    }

                    const newOverrides = dateString.map((date) => ({
                        date,
                        unitPrice: numericValue,
                    }));

                    const mergedOverrides = existingOverrides
                        .filter((o) => !dateString.includes(o.date))
                        .concat(newOverrides);

                    return { ...charge, priceOverrides: mergedOverrides };
                },
            );

            setValue('charges', updatedCharges, { shouldDirty: true });
        } else if (isFlexiblePrice) {
            const overrides = selectedDates.map((date) => {
                return {
                    date: format(date, 'yyyy-MM-dd'),
                    nightPrice: Number(override.nightPrice),
                    dayPrice: Number(override.dayPrice),
                    hourPrice: Number(override.hourPrice),
                };
            });

            const existingOverrides =
                (price as FlexibleExperienceCharge).priceOverrides || [];
            const updatedOverrides = existingOverrides
                .filter((o) => !overrides.some((d) => d.date === o.date))
                .concat(overrides);

            const updatedCharges = charges.map((charge) =>
                charge.chargeType === 'flexiblePrice'
                    ? { ...charge, priceOverrides: updatedOverrides }
                    : charge,
            );
            setValue('charges', updatedCharges, { shouldDirty: true });
        } else {
            const overrides = selectedDates.map((date) => {
                return {
                    date: format(date, 'yyyy-MM-dd'),
                    unitPrice: Number(override.unitPrice),
                };
            });

            const existingOverrides =
                (price as BaseExperienceCharge).priceOverrides || [];
            const updatedOverrides = existingOverrides
                .filter((o) => !overrides.some((d) => d.date === o.date))
                .concat(overrides);

            const updatedCharges = charges.map((charge) =>
                charge.chargeType === 'price'
                    ? { ...charge, priceOverrides: updatedOverrides }
                    : charge,
            );

            setValue('charges', updatedCharges, { shouldDirty: true });
        }

        handleClearSelection();
    };

    const handleDeleteOverride = () => {
        const chargeIndex = charges.findIndex(
            (c) =>
                c?.chargeType === 'price' || c?.chargeType === 'flexiblePrice',
        );

        if (chargeIndex === -1) return;

        const updatedCharges = [...charges];
        const updatedCharge = { ...updatedCharges[chargeIndex] } as any;

        if (updatedCharge.priceOverrides) {
            const dateStrs = selectedDates.map((d) => format(d, 'yyyy-MM-dd'));
            updatedCharge.priceOverrides = updatedCharge.priceOverrides.filter(
                (o: any) => !dateStrs.includes(o.date),
            );
        }

        updatedCharges[chargeIndex] = updatedCharge;
        setValue('charges', updatedCharges, { shouldDirty: true });

        handleClearSelection();
    };

    const calendarDays = useMemo(
        () => buildCalendarDays(visibleMonth),
        [visibleMonth],
    );
    const yearMonths = useMemo(
        () => buildYearMonths(yearViewYear, minVisibleMonth, maxVisibleMonth),
        [yearViewYear, minVisibleMonth, maxVisibleMonth],
    );
    const yearMonthCalendars = useMemo(
        () =>
            yearMonths.map((monthDate) => ({
                monthDate,
                days: buildCalendarDays(monthDate),
            })),
        [yearMonths],
    );

    const showYearMonthDetail = !isTablet;

    const canGoPrevious = visibleMonth > minVisibleMonth;
    const canGoNext = visibleMonth < maxVisibleMonth;

    const handlePreviousMonth = () => {
        if (!canGoPrevious) return;

        setVisibleMonth((prev) => {
            const candidate = startOfMonth(addMonths(prev, -1));
            return candidate < minVisibleMonth ? prev : candidate;
        });
    };

    const handleNextMonth = () => {
        if (!canGoNext) return;

        setVisibleMonth((prev) => {
            const candidate = startOfMonth(addMonths(prev, 1));
            return candidate > maxVisibleMonth ? prev : candidate;
        });
    };

    const handleViewModeChange = (
        _: React.MouseEvent<HTMLElement>,
        nextMode: 'month' | 'year' | null,
    ) => {
        if (!nextMode) return;

        if (nextMode === 'year') {
            setYearViewYear(visibleMonth.getFullYear());
            setRangeStart(null);
            setRangeEnd(null);
            setHoverDate(null);
        } else {
            setSelectedMonths([]);
        }

        setViewMode(nextMode);
    };

    const handleYearToggleChange = (
        _: React.MouseEvent<HTMLElement>,
        nextYear: number | null,
    ) => {
        if (typeof nextYear !== 'number') return;

        setYearViewYear(nextYear);
    };

    const handleYearViewMonthSelect = (monthDate: Date) => {
        setVisibleMonth(monthDate);
        setViewMode('month');
    };

    const incompleteRange = !!(
        selectionMode === 'range' &&
        rangeStart &&
        !rangeEnd
    );

    const hasSelectedDates = !!(
        selectedDates.length > 0 &&
        ((selectionMode === 'range' && rangeEnd) ||
            selectionMode === 'individual')
    );

    const hasSelectedMonths =
        viewMode === 'year' &&
        selectedMonths.length > 0 &&
        !hasYearViewDateSelection;

    // useCallback to memoize the function instance so it doesn't get recreated
    // unless deps change — avoids unnecessary recalculation.
    const getMinimumRatesForPeriod = useCallback(
        (targetDate: Date) => {
            const filterPrefix = format(targetDate, 'yyyy-MM');

            if (isFlexiblePrice) {
                const flexiblePrice = price as FlexibleExperienceCharge;
                let bestOption = {
                    day: flexiblePrice.dayPrice,
                    night: flexiblePrice.nightPrice,
                    hour: flexiblePrice.hourPrice,
                };

                if (flexiblePrice?.priceOverrides?.length) {
                    flexiblePrice.priceOverrides.forEach(
                        (override: FlexiblePriceOverride) => {
                            if (
                                override.date.startsWith(filterPrefix) &&
                                override.nightPrice < bestOption.night
                            ) {
                                bestOption = {
                                    day: override.dayPrice,
                                    night: override.nightPrice,
                                    hour: override.hourPrice,
                                };
                            }
                        },
                    );
                }

                return [
                    {
                        name: 'Day',
                        value: bestOption.day,
                        priceOverridden:
                            flexiblePrice.dayPrice !== bestOption.day,
                    },
                    {
                        name: 'Night',
                        value: bestOption.night,
                        priceOverridden:
                            flexiblePrice.nightPrice !== bestOption.night,
                    },
                    {
                        name: 'Hour',
                        value: bestOption.hour,
                        priceOverridden:
                            flexiblePrice.hourPrice !== bestOption.hour,
                    },
                ];
            }

            return (prices as BaseExperienceCharge[]).map((price) => {
                let minValue = price.unitPrice;

                if (price.priceOverrides?.length) {
                    price.priceOverrides.forEach((override: PriceOverride) => {
                        if (
                            override.date?.startsWith(filterPrefix) &&
                            override.unitPrice < minValue
                        ) {
                            minValue = (override as any).unitPrice;
                        }
                    });
                }

                return {
                    name: price.type,
                    value: minValue,
                    priceOverridden: price.unitPrice !== minValue,
                };
            });
        },
        [isFlexiblePrice, price, prices],
    );

    return (
        <Box sx={{ py: 3 }}>
            <Box
                sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'flex-start',
                    gap: 2,
                }}
            >
                <div>
                    <Typography variant='h3'>Seasonal Pricing</Typography>
                    <Typography
                        variant='body2'
                        sx={{ color: 'text.secondary' }}
                    >
                        This changes based on dates. You can increase or reduce
                        prices for peak periods, holidays, or quiet seasons, and
                        even override individual days as needed. The price you
                        set applies to all future reservations.
                    </Typography>
                </div>
                <Button
                    size='small'
                    variant='outlined'
                    onClick={handleToggleSeasonalPricing}
                    aria-expanded={showSeasonalPricing}
                    sx={{ flexShrink: 0 }}
                >
                    {showSeasonalPricing ? 'Hide' : 'Show'}
                </Button>
            </Box>
            <Collapse in={showSeasonalPricing}>
                <Stack spacing={2} sx={{ pt: 2 }}>
                    <Box
                        sx={{
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: { xs: 'flex-start', sm: 'center' },
                            flexWrap: 'wrap',
                            gap: 1,
                        }}
                    >
                        <Typography
                            sx={{ fontWeight: 'bold', fontSize: '18px' }}
                        ></Typography>
                        <Stack
                            direction='row'
                            sx={{
                                display: 'flex',
                                justifyContent: 'flex-end',
                                alignItems: 'center',
                                flexWrap: 'wrap',
                                gap: 1,
                            }}
                        >
                            <ToggleButtonGroup
                                size='small'
                                exclusive
                                value={viewMode}
                                onChange={handleViewModeChange}
                            >
                                <ToggleButton value='month'>Month</ToggleButton>
                                <ToggleButton value='year'>Year</ToggleButton>
                            </ToggleButtonGroup>
                            <ToggleButtonGroup
                                size='small'
                                exclusive
                                value={selectionMode}
                                onChange={handleSelectionModeChange}
                            >
                                <ToggleButton value='range'>Range</ToggleButton>
                                <ToggleButton value='individual'>
                                    Individual
                                </ToggleButton>
                            </ToggleButtonGroup>
                            {viewMode === 'month' ? (
                                <Box
                                    sx={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: 1,
                                    }}
                                >
                                    <Box
                                        sx={{
                                            display: 'flex',
                                            alignItems: 'center',
                                            gap: 1,
                                        }}
                                    >
                                        <IconButton
                                            aria-label='Previous month'
                                            size='small'
                                            onClick={handlePreviousMonth}
                                            disabled={!canGoPrevious}
                                        >
                                            <ChevronLeft fontSize='small' />
                                        </IconButton>
                                        <Typography
                                            variant='subtitle2'
                                            sx={{
                                                fontWeight: 'bold',
                                                minWidth: '70px',
                                                textAlign: 'center',
                                            }}
                                        >
                                            {format(visibleMonth, 'MMM yyyy')}
                                        </Typography>
                                        <IconButton
                                            aria-label='Next month'
                                            size='small'
                                            onClick={handleNextMonth}
                                            disabled={!canGoNext}
                                        >
                                            <ChevronRight fontSize='small' />
                                        </IconButton>
                                    </Box>

                                    <Button
                                        size='small'
                                        variant='text'
                                        onClick={() =>
                                            setVisibleMonth(startOfMonth(today))
                                        }
                                    >
                                        Today
                                    </Button>
                                </Box>
                            ) : (
                                <ToggleButtonGroup
                                    size='small'
                                    exclusive
                                    value={yearViewYear}
                                    onChange={handleYearToggleChange}
                                >
                                    {yearOptions.map((year) => (
                                        <ToggleButton key={year} value={year}>
                                            {year}
                                        </ToggleButton>
                                    ))}
                                </ToggleButtonGroup>
                            )}
                        </Stack>
                    </Box>

                    {/* using a Grid to wrap the Collapse to prevent flicker/jump when the Collapse is closed */}
                    <Grid>
                        <Collapse
                            in={
                                hasSelectedDates ||
                                incompleteRange ||
                                hasSelectedMonths
                            }
                            timeout='auto'
                            unmountOnExit
                        >
                            <Box
                                sx={{
                                    display: 'flex',
                                    gap: 1,
                                    alignItems: 'center',
                                    justifyContent: 'space-between',
                                    p: 2,
                                    bgcolor: alpha(
                                        theme.palette.primary.main,
                                        0.08,
                                    ),
                                    borderRadius: 2,
                                    minHeight: { xs: '90px', sm: 'auto' },
                                }}
                            >
                                {incompleteRange && (
                                    <>
                                        <Typography sx={{ fontSize: '14px' }}>
                                            Selected:{' '}
                                            {format(rangeStart, 'MMM d, yyyy')}{' '}
                                            - Click end date to complete range
                                        </Typography>
                                        <Button
                                            size='small'
                                            variant='outlined'
                                            onClick={handleClearSelection}
                                        >
                                            Cancel
                                        </Button>
                                    </>
                                )}
                                {hasSelectedDates && (
                                    <>
                                        <Typography
                                            sx={{
                                                fontSize: '14px',
                                                fontWeight: 'bold',
                                            }}
                                        >
                                            {selectedDates.length} date
                                            {selectedDates.length > 1
                                                ? 's'
                                                : ''}{' '}
                                            selected
                                            {selectionMode === 'range' &&
                                                selectedDates.length > 1 && (
                                                    <Typography
                                                        component='span'
                                                        sx={{
                                                            ml: 1,
                                                            fontSize: '14px',
                                                        }}
                                                    >
                                                        (
                                                        {format(
                                                            selectedDates[0],
                                                            'MMM d',
                                                        )}{' '}
                                                        -{' '}
                                                        {format(
                                                            selectedDates[
                                                                selectedDates.length -
                                                                    1
                                                            ],
                                                            'MMM d',
                                                        )}
                                                        )
                                                    </Typography>
                                                )}
                                        </Typography>
                                        <Stack direction='row' spacing={1}>
                                            <Button
                                                size='small'
                                                variant='outlined'
                                                onClick={handleClearSelection}
                                            >
                                                Clear
                                            </Button>
                                            <Button
                                                size='small'
                                                variant='contained'
                                                onClick={() => {
                                                    setModalOpen(true);
                                                }}
                                            >
                                                Set Prices
                                            </Button>
                                        </Stack>
                                    </>
                                )}
                                {hasSelectedMonths && (
                                    <>
                                        <Typography
                                            variant='body3'
                                            sx={{ fontWeight: 600 }}
                                        >
                                            {selectedMonths.length} month
                                            {selectedMonths.length > 1
                                                ? 's'
                                                : ''}{' '}
                                            selected
                                            <Typography
                                                component='span'
                                                variant='body3'
                                                sx={{
                                                    color: 'text.primary',
                                                    ml: 1,
                                                }}
                                            >
                                                ({selectedDates.length} dates
                                                total)
                                            </Typography>
                                        </Typography>
                                        <Stack direction='row' spacing={1}>
                                            <Button
                                                size='small'
                                                variant='outlined'
                                                onClick={handleClearSelection}
                                            >
                                                Clear
                                            </Button>
                                            <Button
                                                size='small'
                                                variant='contained'
                                                onClick={() => {
                                                    setModalOpen(true);
                                                }}
                                            >
                                                Set Prices
                                            </Button>
                                        </Stack>
                                    </>
                                )}
                            </Box>
                        </Collapse>
                    </Grid>

                    {viewMode === 'month' ? (
                        <RatesMonthCalendar
                            monthDate={visibleMonth}
                            days={calendarDays}
                            selectionMode={selectionMode}
                            rangeStart={rangeStart}
                            rangeEnd={rangeEnd}
                            hoverDate={hoverDate}
                            charges={charges}
                            individualDates={individualDates}
                            setIndividualDates={setIndividualDates}
                            setRangeStart={setRangeStart}
                            setRangeEnd={setRangeEnd}
                            setHoverDate={setHoverDate}
                            visibleMonth={visibleMonth}
                            experienceType={experience.type}
                            isYearView={false}
                        />
                    ) : (
                        <Stack spacing={2}>
                            <Box sx={{ overflow: 'hidden' }}>
                                <Grid container spacing={1}>
                                    {yearMonthCalendars.map(
                                        ({ monthDate, days }) => {
                                            const isSelected =
                                                isMonthSelected(monthDate);

                                            return (
                                                <Grid
                                                    item
                                                    xs={6}
                                                    sm={
                                                        experience.type ===
                                                        'commercial'
                                                            ? 6
                                                            : 4
                                                    }
                                                    md={6}
                                                    key={monthDate.toISOString()}
                                                    sx={{
                                                        display: 'flex',
                                                    }}
                                                >
                                                    <Box
                                                        role='button'
                                                        tabIndex={0}
                                                        onClick={(e) => {
                                                            handleMonthToggle(
                                                                monthDate,
                                                                e,
                                                            );
                                                        }}
                                                        onKeyDown={(event) => {
                                                            if (
                                                                event.key ===
                                                                'Enter'
                                                            ) {
                                                                handleMonthToggle(
                                                                    monthDate,
                                                                    event as any,
                                                                );
                                                            }
                                                        }}
                                                        sx={{
                                                            width: '100%',
                                                            border: '1px solid',
                                                            borderColor:
                                                                isSelected
                                                                    ? 'primary.main'
                                                                    : 'divider',
                                                            borderRadius: 2,
                                                            p: 1.5,
                                                            cursor: 'pointer',
                                                            transition:
                                                                'border-color 0.2s, box-shadow 0.2s, background-color 0.2s',
                                                            bgcolor: isSelected
                                                                ? alpha(
                                                                      theme
                                                                          .palette
                                                                          .primary
                                                                          .main,
                                                                      0.2,
                                                                  )
                                                                : 'background.paper',
                                                            '&:hover': {
                                                                borderColor:
                                                                    'primary.main',
                                                                boxShadow: 1,
                                                            },
                                                        }}
                                                    >
                                                        <Box
                                                            sx={{
                                                                display: 'flex',
                                                                justifyContent:
                                                                    'space-between',
                                                                alignItems:
                                                                    'center',
                                                                mb: 1,
                                                            }}
                                                        >
                                                            <Typography
                                                                variant='subtitle2'
                                                                sx={{
                                                                    fontWeight: 600,
                                                                }}
                                                            >
                                                                {format(
                                                                    monthDate,
                                                                    'MMM yyyy',
                                                                )}
                                                            </Typography>
                                                            <CustomTooltip
                                                                title='View this month'
                                                                arrow
                                                            >
                                                                <IconButton
                                                                    size='small'
                                                                    onClick={(
                                                                        e,
                                                                    ) => {
                                                                        e.stopPropagation();
                                                                        handleYearViewMonthSelect(
                                                                            monthDate,
                                                                        );
                                                                    }}
                                                                    sx={{
                                                                        padding: 0.5,
                                                                        '&:hover':
                                                                            {
                                                                                bgcolor:
                                                                                    alpha(
                                                                                        theme
                                                                                            .palette
                                                                                            .primary
                                                                                            .main,
                                                                                        0.1,
                                                                                    ),
                                                                            },
                                                                    }}
                                                                >
                                                                    <ArrowForwardIcon fontSize='small' />
                                                                </IconButton>
                                                            </CustomTooltip>
                                                        </Box>
                                                        {showYearMonthDetail ? (
                                                            <Box
                                                                sx={{
                                                                    mt: 1,
                                                                }}
                                                                onClick={(e) =>
                                                                    e.stopPropagation()
                                                                }
                                                            >
                                                                <RatesMonthCalendar
                                                                    monthDate={
                                                                        monthDate
                                                                    }
                                                                    days={days}
                                                                    selectionMode={
                                                                        selectionMode
                                                                    }
                                                                    rangeStart={
                                                                        rangeStart
                                                                    }
                                                                    rangeEnd={
                                                                        rangeEnd
                                                                    }
                                                                    hoverDate={
                                                                        hoverDate
                                                                    }
                                                                    charges={
                                                                        charges
                                                                    }
                                                                    individualDates={
                                                                        individualDates
                                                                    }
                                                                    setIndividualDates={(
                                                                        dates,
                                                                    ) => {
                                                                        setSelectedMonths(
                                                                            [],
                                                                        );
                                                                        setIndividualDates(
                                                                            dates,
                                                                        );
                                                                    }}
                                                                    setRangeStart={(
                                                                        date,
                                                                    ) => {
                                                                        if (
                                                                            date
                                                                        ) {
                                                                            setSelectedMonths(
                                                                                [],
                                                                            );
                                                                        }
                                                                        setRangeStart(
                                                                            date,
                                                                        );
                                                                    }}
                                                                    setRangeEnd={
                                                                        setRangeEnd
                                                                    }
                                                                    setHoverDate={
                                                                        setHoverDate
                                                                    }
                                                                    visibleMonth={
                                                                        monthDate
                                                                    }
                                                                    experienceType={
                                                                        experience.type
                                                                    }
                                                                    isYearView={
                                                                        true
                                                                    }
                                                                />
                                                            </Box>
                                                        ) : (
                                                            <Stack
                                                                spacing={0.25}
                                                                sx={{
                                                                    mt: 1,
                                                                }}
                                                            >
                                                                {getMinimumRatesForPeriod(
                                                                    monthDate,
                                                                ).map(
                                                                    ({
                                                                        name,
                                                                        value,
                                                                        priceOverridden,
                                                                    }: {
                                                                        name: string;
                                                                        value: number;
                                                                        priceOverridden: boolean;
                                                                    }) => (
                                                                        <Box
                                                                            key={
                                                                                name
                                                                            }
                                                                            sx={{
                                                                                display:
                                                                                    'flex',
                                                                                justifyContent:
                                                                                    'space-between',
                                                                            }}
                                                                        >
                                                                            <Typography
                                                                                sx={{
                                                                                    color: 'text.primary',
                                                                                    fontSize:
                                                                                        '14px',
                                                                                }}
                                                                            >
                                                                                {renderRateLabel(
                                                                                    name,
                                                                                )}
                                                                            </Typography>
                                                                            <Typography
                                                                                sx={{
                                                                                    color: 'text.primary',
                                                                                    fontSize:
                                                                                        '14px',
                                                                                    textWrap:
                                                                                        'nowrap',
                                                                                }}
                                                                            >
                                                                                {priceOverridden && (
                                                                                    <Typography
                                                                                        component='span'
                                                                                        sx={{
                                                                                            fontSize:
                                                                                                '11px',
                                                                                        }}
                                                                                    >
                                                                                        from{' '}
                                                                                    </Typography>
                                                                                )}
                                                                                {formatCurrencyShort(
                                                                                    value,
                                                                                )}
                                                                            </Typography>
                                                                        </Box>
                                                                    ),
                                                                )}
                                                            </Stack>
                                                        )}
                                                    </Box>
                                                </Grid>
                                            );
                                        },
                                    )}
                                </Grid>
                            </Box>
                        </Stack>
                    )}
                </Stack>
            </Collapse>

            <PriceOverrideModal
                open={modalOpen}
                onClose={() => setModalOpen(false)}
                selectedDate={
                    selectedDates.length === 1 ? selectedDates[0] : null
                }
                selectedDatesCount={selectedDates.length}
                isFlexiblePrice={isFlexiblePrice}
                onSave={handleSaveOverride}
                onDelete={handleDeleteOverride}
                experienceType={experience.type}
                shouldShowHalfDayBilling={shouldShowHalfDayBilling}
            />
        </Box>
    );
};

export default RatesCalendar;
